import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Menu, Popover } from 'antd';
import { TbUserCheck, TbUsers } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DropdownOptionContent, DropdownPanel, DropdownPanelMenu } from '@/shared/components/dropdowns';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import {
    selectFetchMembers,
    selectMembersLoading,
    useActiveWorkspaceId,
    useActiveWorkspaceMembers,
    useMemberStore,
} from '@/stores';
import { Member, TaskAssignee } from '@/types';

import EmptySection from '../../layouts/views/EmptySection';
import ListItemSkeleton from '../../skeletons/ListItemSkeleton';
import TaskParticipantAvatarGroup from '../TaskParticipantAvatarGroup';

interface TaskParticipantPickerProps {
    disabled?: boolean;
    label: string;
    onChange: (memberIds: string[]) => void;
    participants?: TaskAssignee[];
    selectedMemberIds: string[];
    variant: 'assignee' | 'reviewer';
}

const memberName = (member: Member) =>
    [member.user?.firstName, member.user?.lastName].filter(Boolean).join(' ') || member.user?.emailAddress || 'Member';

const TaskParticipantPicker: FC<TaskParticipantPickerProps> = ({
    disabled,
    label,
    onChange,
    participants = [],
    selectedMemberIds,
    variant,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const fetchMembers = useMemberStore(selectFetchMembers);
    const loading = useMemberStore(selectMembersLoading);
    const activeMembers = useActiveWorkspaceMembers();
    const activeWorkspaceId = useActiveWorkspaceId();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const refreshedWorkspaceIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (!open) {
            refreshedWorkspaceIdRef.current = null;
            return;
        }

        if (!activeWorkspaceId || loading || refreshedWorkspaceIdRef.current === activeWorkspaceId) {
            return;
        }

        refreshedWorkspaceIdRef.current = activeWorkspaceId;
        void fetchMembers();
    }, [activeWorkspaceId, fetchMembers, loading, open]);

    const filteredMembers = useMemo(() => {
        const normalizedQuery = query.trim().toLocaleLowerCase();
        return activeMembers
            .filter((member) => {
                if (!normalizedQuery) {
                    return true;
                }

                return `${memberName(member)} ${member.user?.emailAddress ?? ''}`
                    .toLocaleLowerCase()
                    .includes(normalizedQuery);
            })
            .sort((left, right) => memberName(left).localeCompare(memberName(right)));
    }, [activeMembers, query]);

    const toggleMember = (memberId: string) => {
        onChange(
            selectedMemberIds.includes(memberId)
                ? selectedMemberIds.filter((selectedId) => selectedId !== memberId)
                : [...selectedMemberIds, memberId],
        );
    };
    const icon = variant === 'assignee' ? <TbUsers /> : <TbUserCheck />;
    const selectedParticipants = participants.filter((participant) =>
        selectedMemberIds.includes(participant.member.id),
    );
    const participantLabel = variant === 'assignee' ? 'Assignee' : 'Reviewer';
    const selectedLabel =
        selectedMemberIds.length > 0
            ? `${selectedMemberIds.length} ${participantLabel}${selectedMemberIds.length === 1 ? '' : 's'}`
            : label;

    return (
        <Popover
            arrow={false}
            content={
                <DropdownPanel
                    maxHeight='18rem'
                    search={{
                        placeholder: `Search ${label}...`,
                        setValue: setQuery,
                        value: query,
                    }}
                    width='18rem'
                >
                    {loading ? (
                        <div aria-label={`Loading ${label.toLocaleLowerCase()}`} style={{ minHeight: '8rem' }}>
                            {Array.from({ length: 4 }, (_, index) => (
                                <ListItemSkeleton
                                    avatar
                                    compact
                                    key={index}
                                    lineWidths={['68%', '44%']}
                                    style={{ padding: '0.5rem 0.75rem' }}
                                />
                            ))}
                        </div>
                    ) : filteredMembers.length === 0 ? (
                        <EmptySection
                            description='Try another name or email address.'
                            height='8rem'
                            icon={
                                variant === 'assignee' ? (
                                    <TbUsers size='1.25rem' style={{ color: token.colorTextTertiary }} />
                                ) : (
                                    <TbUserCheck size='1.25rem' style={{ color: token.colorTextTertiary }} />
                                )
                            }
                            label={`No ${label} Found`}
                        />
                    ) : (
                        <DropdownPanelMenu selectable={false}>
                            {filteredMembers.map((member) => {
                                const name = memberName(member);

                                return (
                                    <Menu.Item key={member.id} onClick={() => toggleMember(member.id)}>
                                        <DropdownOptionContent
                                            avatar={{
                                                props: { children: name.slice(0, 1), shape: 'circle', size: 24 },
                                                src: member.user?.avatar,
                                            }}
                                            metadata={
                                                member.user?.emailAddress ? [member.user.emailAddress] : undefined
                                            }
                                            selected={selectedMemberIds.includes(member.id)}
                                            title={name}
                                        />
                                    </Menu.Item>
                                );
                            })}
                        </DropdownPanelMenu>
                    )}
                </DropdownPanel>
            }
            destroyOnHidden
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                    setQuery('');
                }
            }}
            open={open}
            placement='bottomLeft'
            trigger='click'
        >
            <DropdownButton disabled={disabled} size='small'>
                {selectedParticipants.length > 0 ? (
                    <TaskParticipantAvatarGroup maxCount={3} participants={selectedParticipants} size={16} />
                ) : (
                    icon
                )}
                {selectedLabel}
            </DropdownButton>
        </Popover>
    );
};

export default TaskParticipantPicker;
